import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { placementKey } from '~/domain/patient/imageBinding';
import {
    BlendCandidate,
    TileAlignment,
    TileGeometryLink,
} from '../adapters/types';
import {
    DatasetRow,
    PRIMARY_ROW_INDEX,
    RowLock,
    RowLockMode,
} from '../layout/types';

/**
 * How a row lies on the primary row.
 *
 * - `none`: the row has nothing to lock to. It is the primary row, it shows
 *   the primary image itself (and so already shares its view), or there is
 *   no primary image.
 * - `unavailable`: the row's image is neither on the primary's frame of
 *   reference nor registered to it, so it can never be locked.
 * - `independent`: it could be locked through one of `candidates`, and is not.
 * - `locked`: it follows the primary row through `current`, as `mode` says.
 */
export type RowLockState =
    | { readonly kind: 'none' }
    | { readonly kind: 'unavailable' }
    | {
          readonly kind: 'independent';
          readonly candidates: ReadonlyArray<BlendCandidate>;
      }
    | {
          readonly kind: 'locked';
          readonly current: BlendCandidate;
          readonly candidates: ReadonlyArray<BlendCandidate>;
          readonly mode: RowLockMode;
      };

/** The registration a candidate binds through, or undefined for the same frame. */
function registrationIdOf(candidate: BlendCandidate): string | undefined {
    return candidate.binding.kind === 'mapped'
        ? candidate.binding.registrationId
        : undefined;
}

/**
 * The binding a row locks through until the user chooses: the shared frame
 * of reference when there is one, else the first approved registration,
 * else the first unapproved one, in the order the candidates are listed. A
 * rejected registration is never chosen for the user; it can still be
 * chosen by hand.
 */
function defaultCandidate(
    candidates: ReadonlyArray<BlendCandidate>
): BlendCandidate | undefined {
    const withStatus = (status: DICOMApprovalStatus) =>
        candidates.find(
            (candidate) =>
                candidate.binding.kind === 'mapped' &&
                candidate.binding.approvalStatus === status
        );
    return (
        candidates.find((candidate) => candidate.binding.kind === 'identity') ??
        withStatus(DICOMApprovalStatus.APPROVED) ??
        withStatus(DICOMApprovalStatus.UNAPPROVED)
    );
}

/**
 * Resolves how the row at `rowIndex` lies on the primary row showing
 * `primarySeriesUid`. `primaryCandidates` are the images the primary image
 * may be paired with, each with the binding that places it; the row's own
 * image's entries among them are what the row may lock through.
 *
 * A row locked through a registration that no longer places its image is
 * independent: it never moves to another registration unasked. So is a row
 * the grid locked through a registration since rejected.
 */
export function rowLockStateOf(
    row: DatasetRow,
    rowIndex: number,
    primarySeriesUid: SeriesInstanceUid | undefined,
    primaryCandidates: ReadonlyArray<BlendCandidate>
): RowLockState {
    const seriesUid = row.dataset?.seriesInstanceUid;
    if (
        rowIndex === PRIMARY_ROW_INDEX ||
        seriesUid === undefined ||
        primarySeriesUid === undefined ||
        seriesUid === primarySeriesUid
    ) {
        return { kind: 'none' };
    }
    const candidates = primaryCandidates.filter(
        (candidate) => candidate.seriesInstanceUid === seriesUid
    );
    if (candidates.length === 0) {
        return { kind: 'unavailable' };
    }
    const lock = row.lock;
    if (lock?.kind === 'independent') {
        return { kind: 'independent', candidates };
    }
    const current =
        lock === undefined
            ? defaultCandidate(candidates)
            : candidates.find(
                  (candidate) =>
                      registrationIdOf(candidate) === lock.registrationId
              );
    const rejectedDefault =
        lock?.kind === 'locked' &&
        lock.byDefault &&
        current?.binding.kind === 'mapped' &&
        current.binding.approvalStatus === DICOMApprovalStatus.REJECTED;
    return current && !rejectedDefault
        ? {
              kind: 'locked',
              current,
              candidates,
              mode: (lock?.kind === 'locked' && lock.mode) || 'geometry',
          }
        : { kind: 'independent', candidates };
}

/** The lock that holds a row to the primary row through `candidate`, as `mode` says. */
export function lockThrough(
    candidate: BlendCandidate,
    mode: RowLockMode
): Extract<RowLock, { kind: 'locked' }> {
    return {
        kind: 'locked',
        registrationId: registrationIdOf(candidate),
        mode,
    };
}

/**
 * The part the tiles of the row at `rowIndex`, in `state`, take in keeping
 * tiles of the primary image at one scale and centre: the primary row's
 * lead, a row locked on zoom and position follows, and any other row
 * keeps its own.
 */
export function tileGeometryLinkOf(
    state: RowLockState,
    rowIndex: number
): TileGeometryLink | undefined {
    if (rowIndex === PRIMARY_ROW_INDEX) {
        return 'lead';
    }
    return state.kind === 'locked' && state.mode === 'zoomAndPosition'
        ? 'follow'
        : undefined;
}

/** How the tiles of a row in `state` mount: on the primary's view when locked. */
export function tileAlignmentOf(
    state: RowLockState,
    primarySeriesUid: SeriesInstanceUid | undefined
): TileAlignment | undefined {
    return state.kind === 'locked' && primarySeriesUid !== undefined
        ? { anchorSeriesUid: primarySeriesUid, binding: state.current.binding }
        : undefined;
}

/**
 * The binding through which an independent row in `state` draws the
 * primary's structure set, for reading only: the one a lock would choose
 * by default. Undefined for any other row, and for one whose only
 * registrations are rejected.
 */
export function readOnlyStructuresThrough(
    state: RowLockState
): BlendCandidate | undefined {
    return state.kind === 'independent'
        ? defaultCandidate(state.candidates)
        : undefined;
}

/**
 * How the tiles of an independent row in `state` place the primary's
 * structure set on their own view: through
 * {@link readOnlyStructuresThrough}. Undefined where that is.
 */
export function tileStructureSourceOf(
    state: RowLockState,
    primarySeriesUid: SeriesInstanceUid | undefined
): TileAlignment | undefined {
    const through = readOnlyStructuresThrough(state);
    return through && primarySeriesUid !== undefined
        ? { anchorSeriesUid: primarySeriesUid, binding: through.binding }
        : undefined;
}

/**
 * A key that changes exactly when a tile must mount on another view: a
 * different anchor or a different registration. The approval status a
 * binding carries is not part of it, so approving a registration never
 * remounts the tiles that follow it.
 */
export function tileAlignmentKey(
    alignment: TileAlignment | undefined
): string | undefined {
    return (
        alignment && placementKey(alignment.anchorSeriesUid, alignment.binding)
    );
}
